import React, { PropsWithChildren, useCallback, useMemo } from "react";
import { BacktestDetailsContext } from "@shared/features/bots/BacktestDetails.context";
import useBacktestingDrawer from "@shared/features/bots/BotForm/Backtesting/useBacktestingDrawer";
import useLiveBacktesting from "@shared/features/bots/BotForm/Backtesting/useLiveBacktesting";
import { preprocessTradesData } from "@shared/features/trades/common/utils";
import { BacktestingRunType, TradeData } from "@shared/types";

interface BacktestDetailsProviderProps extends PropsWithChildren {
  backtestingBotId?: number;
  backtestingId?: number;
  backtestingRun?: BacktestingRunType;
}

const BacktestDetailsProvider = ({
  backtestingBotId,
  backtestingId,
  backtestingRun: propsBacktestingRun,
  children,
}: BacktestDetailsProviderProps) => {
  const {
    botData,
    backtestingRun,
    loading,
    backtestingJobResults,
    liveBarChartData,
  } = useBacktestingDrawer({
    backtestingBotId,
    backtestingId,
  });

  const backtestingRunToUse = propsBacktestingRun ?? backtestingRun;
  const { liveBacktestingData, inProgress } = useLiveBacktesting({
    data: backtestingRunToUse,
  });

  const exchangesToClientsMapping = useMemo(
    () => (botData ? { [botData.exchange]: [botData.client_name] } : undefined),
    [botData]
  );

  const getLiveTradesData = useCallback(
    (isOpenTrades?: boolean): TradeData[] | undefined => {
      const key = isOpenTrades ? "open_trades_data" : "closed_trades_data";
      return backtestingJobResults && exchangesToClientsMapping
        ? preprocessTradesData(
            // @ts-ignore
            backtestingJobResults?.[key]?.data,
            // @ts-ignore
            backtestingJobResults?.[key]?.headers || [],
            exchangesToClientsMapping
          )
        : undefined;
    },
    [backtestingJobResults, exchangesToClientsMapping]
  );

  const openTradesData = useMemo(
    () => getLiveTradesData(true),
    [getLiveTradesData]
  );
  const closedTradesData = useMemo(
    () => getLiveTradesData(false),
    [getLiveTradesData]
  );

  const contextData = useMemo(() => {
    return {
      botData,
      backtestingRun: backtestingRunToUse,
      loading,
      backtestingJobResults,
      liveBarChartData,
      liveBacktestingData,
      inProgress,
      openTradesData,
      closedTradesData,
    };
  }, [
    botData,
    backtestingRunToUse,
    loading,
    backtestingJobResults,
    liveBarChartData,
    liveBacktestingData,
    inProgress,
    openTradesData,
    closedTradesData,
  ]);

  return (
    <BacktestDetailsContext.Provider value={contextData}>
      {children}
    </BacktestDetailsContext.Provider>
  );
};

export default BacktestDetailsProvider;
